import React from 'react';
import {
    Box,
    Typography,
    Button,
    Table,
    TableContainer,
    Select,
    MenuItem,
    TextField,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    Checkbox,
    FormControlLabel
} from '@mui/material';

import useBatchStatus from './useBatchStatus';
import RenderIf from '../../../renderIf';
import DashboardHeader from '../../dashboardHeader';
import DashboardBody from '../../dashboardBody';
import DatePicker from '../../datePick';
import { RequiredMarker } from '../../muiControls';


const BatchStatusUpdate = ({ open = false, selected = [], closeCallback, ctx }) => {
    const {
        chosenStatus,
        changeReason,
        statusDate,
        suppressNotifications,
        step,
        inError,
        statuses,
        validationHeaders,
        scholarshipDataHeaders,
        isDoingAjax,
        scholarshipData,
        showCalcs,
        showDate,
        showDateError,
        showChangeReasonError,
        reasonRequired,
        retrieveStatuses,
        sendBatchUpdate,
        handleStatusChange,
        handleChangeReasonChange,
        handleChangeReasonBlur,
        handleSuppressNotifications,
        handleDateChange,
        handleDateError,
        handleDateBlur,

        resetModal,
        changeStep,
    } = useBatchStatus(ctx, ctx === 'application' ? selected?.[0]?.Status?.id : selected?.[0]?.InvoiceStatusID);

    const AmountMessage = () => (
        <Box
            component="span"
            sx={{
                '&.MuiBox-root': {
                    color: (theme) => `${theme.palette.grey['field-text']}`,
                    fontWeight: 700,
                },
            }}
        >
            {selected.length} {ctx === "application" ? `${ctx}s` : ctx}
        </Box>
    );

    const handlePrimary = async () => {
        if (step === 2) {
            if (await sendBatchUpdate?.(chosenStatus, changeReason, selected)) {
                closeCallback?.(true);
                resetModal?.();
            }
        } else {
            changeStep(2);
        }
    };

    const handleCancel = () => {
        resetModal();
        closeCallback?.(false);
    };

    React.useEffect(() => {
        if (open) {
            retrieveStatuses();
        }
    }, [open]);

    const strItems = ctx === "application" ? `${ctx}s` : ctx;
    const strFirstUpper = `${strItems[0].toUpperCase()}${strItems.slice(1)}`;

    return (
        <Dialog open={open}>
            <DialogTitle>Update status of selected {strItems}</DialogTitle>
            <DialogContent>
                <Box>
                    <RenderIf isTrue={step === 1}>
                        <Typography
                            sx={{
                                '& b': {
                                    color: (theme) => theme.palette.primary.main,
                                },
                            }}
                        >
                            Below are the <AmountMessage /> you've selected for a status update. Please validate these are the correct {strItems}
                            , and then click <b>Select Status</b> to choose the new status for the {strItems}.
                        </Typography>

                        <RenderIf isTrue={isDoingAjax}>
                            <CircularProgress size="16px" /> Loading...
                        </RenderIf>

                        <RenderIf isTrue={!isDoingAjax}>
                            <TableContainer
                                sx={{
                                    overflow: 'visible',
                                }}
                            >
                                <Table
                                    stickyHeader
                                    size="small"
                                >
                                    <DashboardHeader headers={validationHeaders} />

                                    <DashboardBody
                                        headers={validationHeaders}
                                        list={selected}
                                        isFetching={false}
                                        handleNav={() => null}
                                    />
                                </Table>
                            </TableContainer>
                        </RenderIf>
                    </RenderIf>

                    <RenderIf isTrue={step === 2}>
                        <Typography
                            sx={{
                                '& b': {
                                    color: (theme) => theme.palette.primary.main,
                                },
                            }}
                        >
                            Select a new status for the invoice(s). and click on <b>Update Status</b>. Depending on the new status you may be required to complete the "Reason for Change" field.
                        </Typography>

                        <Typography
                            sx={{
                                color: (theme) => theme.palette.grey.header,
                                fontWeight: 600,
                                fontSize: 'calc(14rem/16)',
                            }}
                        >
                            <RequiredMarker /> New Status:
                        </Typography>
                        <Select
                            id="new-status-id"
                            sx={{
                                width: 'calc(170rem/16)',
                                mb: 'calc(20rem/16)',
                                '& > div': {
                                    width: '100%',
                                    padding: 'calc(8rem/16) calc(9rem/16)',
                                },
                            }}
                            value={chosenStatus}
                            onChange={(event) => {
                                handleStatusChange(event.target.value, selected);
                            }}
                            size="small"
                            disabled={isDoingAjax}
                        >
                            <MenuItem
                                sx={{ height: 32 }}
                                value=""
                            ></MenuItem>
                            {statuses.map((item) => {
                                return <MenuItem value={item.StatusID}>{item.Description}</MenuItem>;
                            })}
                        </Select>

                        <RenderIf isTrue={showCalcs}>
                            <TableContainer
                                sx={theme => {
                                    return {
                                        overflow: 'visible',
                                        mb: theme.spacing(3),
                                    };
                                }}
                            >
                                <Table
                                    stickyHeader
                                    size="small"
                                >
                                    <DashboardHeader headers={scholarshipDataHeaders} />

                                    <DashboardBody
                                        headers={scholarshipDataHeaders}
                                        list={scholarshipData}
                                        isFetching={false}
                                        handleNav={() => null}
                                    />
                                </Table>
                            </TableContainer>
                        </RenderIf>

                        <RenderIf isTrue={showDate}>
                            <Box
                                sx={theme=> {
                                    return {
                                        pb: theme.spacing(2),
                                    }
                                }}
                            >
                                <Typography
                                    sx={{
                                        color: (theme) => theme.palette.grey.header,
                                        fontWeight: 600,
                                        fontSize: 'calc(14rem/16)',
                                    }}
                                >
                                    <RequiredMarker /> Date Approved:
                                </Typography>
                                <DatePicker
                                    value={statusDate}
                                    clearable
                                    size="small"
                                    disabled={isDoingAjax}
                                    onChange={(newVal) => handleDateChange(newVal)}
                                    onError={(err, newVal) => handleDateError(err, newVal)}
                                    onBlur={(event) => {
                                        if(event?.type === 'blur'){
                                            handleDateBlur();
                                        }
                                    }}
                                    error={showDateError && inError?.find?.(err => err?.id === 'statusDate')}
                                    textFieldSx={{
                                        '.Mui-disabled': {
                                            fontSize: '0.9375rem',
                                            WebkitTextFillColor: 'unset',
                                            color: (theme) => theme.palette.grey['field-text'],
                                            opacity: 1,
                                        },
                                    }}
                                    aria-describedby='status-date-error'
                                />
                                
                                <RenderIf isTrue={showDateError && inError?.find?.(err => err?.id === 'statusDate')?.message}>
                                    <Typography
                                        id='status-date-error'
                                        color='error'
                                        sx={(theme) => {
                                            return {
                                                fontSize: theme.spacing(1.5),
                                                ml: theme.spacing(0.25),
                                            };
                                        }}
                                    >
                                        {inError?.find?.(err => err?.id === 'statusDate')?.message}
                                    </Typography>
                                </RenderIf>
                            </Box>
                        </RenderIf>

                        <Box>
                            <Typography
                                sx={{
                                    color: (theme) => theme.palette.grey.header,
                                    fontWeight: 600,
                                    fontSize: 'calc(14rem/16)',
                                }}
                            >
                                {reasonRequired ? (
                                    <Box
                                        component="span"
                                        sx={{ color: (theme) => theme.palette.error.main }}
                                    >
                                        *&nbsp;
                                    </Box>
                                ) : null}
                                Reason for Change:
                            </Typography>
                            <TextField
                                value={changeReason}
                                onChange={(e) => handleChangeReasonChange(e.target.value)}
                                onBlur={handleChangeReasonBlur}
                                multiline
                                fullWidth
                                rows={4}
                                sx={{
                                    width: '100%',
                                }}
                                size="small"
                                disabled={isDoingAjax}
                                error={showChangeReasonError && inError?.find(err => err?.id === 'reasonRequired')}
                                errorMessage={showChangeReasonError && inError?.find(err => err?.id === 'reasonRequired')?.message}
                                aria-describedby='change-reason-error'
                            />

                            <RenderIf isTrue={showChangeReasonError && inError?.find(err => err?.id === 'reasonRequired')?.message}>
                                <Typography
                                    id="change-reason-error"
                                    color='error'
                                    sx={(theme) => {
                                        return {
                                            fontSize: theme.spacing(1.5),
                                        };
                                    }}
                                >
                                    {inError?.find(err => err?.id === 'reasonRequired')?.message}
                                </Typography>
                            </RenderIf>
                        </Box>
                    </RenderIf>

                    <RenderIf isTrue={ctx === 'application' && step === 2}>
                        <FormControlLabel
                            key="notifications-checkbox"
                            control={
                                <Checkbox
                                    value={suppressNotifications}
                                    onChange={(event) => handleSuppressNotifications(event.target.checked)}
                                />
                            }
                            label={'Do not send notifications'}
                            disabled={isDoingAjax}
                        />
                    </RenderIf>
                </Box>

                <Box>
                    <Button
                        onClick={() => handleCancel()}
                        variant="contained"
                        color="secondary"
                    >
                        Cancel
                    </Button>

                    <Button
                        onClick={() => handlePrimary()}
                        variant="contained"
                        disabled={
                            step === 2 && (isDoingAjax || chosenStatus === '' || inError?.length > 0)
                        }
                    >
                        {step === 1 ? 'Select Status' : `Update ${strFirstUpper}`}
                    </Button>
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default BatchStatusUpdate;
